import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AppWindow,
  ArrowRight,
  BadgeCheck,
  Calculator,
  Check,
  Database,
  FileSearch,
  Gauge,
  Layers,
  Lock,
  Search,
  ShieldCheck,
  Target,
} from 'lucide-react';
import { AlOneLayerStack } from './AlOneLayerStack';
import { CountUp } from './CountUp';
import { alaViewport } from './alaMotion';
import { plainText } from './propertyUtils';

const investorsCtaDef: SlotGroupDefinition = {
  label: 'Investors CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/investors' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Explore investing with us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const contactCtaDef: SlotGroupDefinition = {
  label: 'Contact CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/investors#investor-contact' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Talk to capital markets' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const EASE = [0.22, 1, 0.36, 1] as const;

/** Illustrative rows for the animated audit-trail panel (decorative only). */
const AUDIT_ROWS = [
  { metric: 'Occupancy rate', source: 'Property management system', stamp: '09:14:02' },
  { metric: 'Trailing-12 NOI', source: 'General ledger export', stamp: '09:14:05' },
  { metric: 'Market rent comps', source: 'Third-party market feed', stamp: '09:14:09' },
  { metric: 'CapEx schedule', source: 'Construction draw log', stamp: '09:14:12' },
  { metric: 'Lease expirations', source: 'Rent roll', stamp: '09:14:16' },
] as const;

const AUDIT_STEPS = ['Ingested', 'Traced to source', 'Verified'] as const;

const EYEBROW_CLASS = 'font-heading text-sm font-medium uppercase tracking-[0.12em] text-[rgb(var(--ala-text-muted))]';
const EYEBROW_LIGHT_CLASS =
  'font-heading text-sm font-medium uppercase tracking-[0.12em] text-[rgb(var(--ala-on-primary))]/70 [&_p]:text-[rgb(var(--ala-on-primary))]/70';
const STAGE_NUM_CLASS =
  'font-heading text-[56px] font-bold leading-none tracking-[-0.04em] text-[rgb(var(--ala-secondary))]/20 [&_p]:text-[rgb(var(--ala-secondary))]/20';
const STAGE_TITLE_CLASS =
  'font-heading text-[clamp(26px,2.3vw,36px)] font-medium leading-[1.1] tracking-[-0.03em] text-[rgb(var(--ala-text))]';
const CARD_BODY_CLASS = 'font-heading text-base font-normal leading-[1.6] text-[rgb(var(--ala-text-muted))]';
const PILLAR_TITLE_CLASS =
  'font-heading text-[clamp(22px,1.8vw,28px)] font-medium leading-[1.15] tracking-[-0.02em] text-[rgb(var(--ala-text))]';
const AI_POINT_CLASS =
  'font-heading text-base font-medium leading-[1.5] text-[rgb(var(--ala-on-primary))] [&_p]:text-[rgb(var(--ala-on-primary))]';

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="h-2 w-2 rounded bg-[rgb(var(--ala-accent))]" aria-hidden />
      {children}
    </div>
  );
}

function AiPoint({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-[rgb(var(--ala-secondary))]" aria-hidden />
      {children}
    </li>
  );
}

function LifecycleCard({
  index,
  icon: Icon,
  num,
  title,
  body,
}: {
  index: number;
  icon: ComponentType<{ className?: string }>;
  num: ReactNode;
  title: ReactNode;
  body: ReactNode;
}) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.7, delay: index * 0.12, ease: EASE }}
      className="group relative flex flex-col gap-10 overflow-clip rounded-[16px] border border-[rgba(10,17,25,0.08)] bg-[rgb(var(--ala-background))] p-8 shadow-sm transition-shadow duration-500 hover:shadow-2xl md:p-10"
    >
      <div
        className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-[rgb(var(--ala-secondary))]/0 blur-3xl transition-colors duration-700 group-hover:bg-[rgb(var(--ala-secondary))]/25"
        aria-hidden
      />
      <div className="relative flex items-center justify-between">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[rgb(var(--ala-dark))] text-[rgb(var(--ala-secondary))] transition-transform duration-500 group-hover:scale-110">
          <Icon className="h-6 w-6" />
        </span>
        {num}
      </div>
      <div className="relative flex flex-col gap-4">
        {title}
        {body}
      </div>
      <span
        className="absolute bottom-0 left-0 h-[3px] w-0 bg-[rgb(var(--ala-secondary))] transition-all duration-700 group-hover:w-full"
        aria-hidden
      />
    </motion.article>
  );
}

function AuditTrailPanel() {
  const [tick, setTick] = useState(0);
  const total = AUDIT_ROWS.length * AUDIT_STEPS.length;

  useEffect(() => {
    const timer = setInterval(() => setTick((t) => (t + 1) % (total + 2)), 700);
    return () => clearInterval(timer);
  }, [total]);

  return (
    <div
      className="relative w-full overflow-clip rounded-[16px] border border-[rgb(var(--ala-on-primary))]/10 bg-[#071a2e]/80 shadow-2xl backdrop-blur"
      aria-hidden
    >
      <div className="flex items-center justify-between border-b border-[rgb(var(--ala-on-primary))]/10 px-5 py-4">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-[rgb(var(--ala-accent))]/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-[rgb(var(--ala-on-primary))]/20" />
          <span className="h-2.5 w-2.5 rounded-full bg-[rgb(var(--ala-on-primary))]/20" />
        </div>
        <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-[rgb(var(--ala-on-primary))]/50">
          al-one · audit trail
        </span>
      </div>
      <ul className="flex flex-col divide-y divide-[rgb(var(--ala-on-primary))]/5">
        {AUDIT_ROWS.map((row, r) => {
          const stepsDone = Math.max(0, Math.min(AUDIT_STEPS.length, tick - r * AUDIT_STEPS.length));
          const verified = stepsDone === AUDIT_STEPS.length;
          return (
            <li key={row.metric} className="grid grid-cols-[1fr_auto] items-center gap-4 px-5 py-4">
              <div className="flex min-w-0 flex-col gap-1">
                <span className="truncate font-heading text-[15px] font-semibold text-[rgb(var(--ala-on-primary))]">
                  {row.metric}
                </span>
                <span className="truncate font-mono text-[11px] text-[rgb(var(--ala-on-primary))]/45">
                  src: {row.source} · {row.stamp}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                {AUDIT_STEPS.map((step, s) => (
                  <span
                    key={step}
                    title={step}
                    className={`h-1.5 w-6 rounded-full transition-colors duration-300 ${
                      s < stepsDone ? 'bg-[rgb(var(--ala-secondary))]' : 'bg-[rgb(var(--ala-on-primary))]/10'
                    }`}
                  />
                ))}
                <AnimatePresence>
                  {verified ? (
                    <motion.span
                      initial={{ opacity: 0, scale: 0.6 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      className="ml-2 flex h-6 w-6 items-center justify-center rounded-full bg-[rgb(var(--ala-secondary))] text-[rgb(var(--ala-on-secondary))]"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </motion.span>
                  ) : (
                    <span className="ml-2 h-6 w-6 rounded-full border border-[rgb(var(--ala-on-primary))]/15" />
                  )}
                </AnimatePresence>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="flex items-center justify-between border-t border-[rgb(var(--ala-on-primary))]/10 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.14em]">
        <span className="text-[rgb(var(--ala-on-primary))]/50">
          {AUDIT_STEPS[Math.min(AUDIT_STEPS.length - 1, tick % AUDIT_STEPS.length)]}…
        </span>
        <span className="text-[rgb(var(--ala-secondary))]">0 unsourced values</span>
      </div>
    </div>
  );
}

function PillarCard({
  index,
  icon: Icon,
  title,
  body,
}: {
  index: number;
  icon: ComponentType<{ className?: string }>;
  title: ReactNode;
  body: ReactNode;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: EASE }}
      className="group flex flex-col gap-5 border-t border-[rgba(10,17,25,0.12)] pt-8"
    >
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[rgb(var(--ala-secondary))]/10 text-[rgb(var(--ala-secondary))] transition-colors duration-300 group-hover:bg-[rgb(var(--ala-secondary))] group-hover:text-[rgb(var(--ala-on-secondary))]">
        <Icon className="h-5 w-5" />
      </span>
      {title}
      {body}
    </motion.div>
  );
}

export function AlOneContent() {
  const investorsCta = useSlotGroup('alonecontent.investors_cta', investorsCtaDef);
  const contactCta = useSlotGroup('alonecontent.contact_cta', contactCtaDef);
  const accuracy = plainText(useContentToken('alonecontent.accuracy_value')) || '100%';

  return (
    <>
      {/* Three-pronged AI architecture (overall tech approach) */}
      <section
        id="al-one-content"
        className="w-full scroll-mt-24 bg-[rgb(var(--ala-surface))] px-6 py-20 md:px-[clamp(16px,6.7vw,96px)] md:py-[clamp(80px,10vw,140px)]"
      >
        <div className="mx-auto flex w-full max-w-[1248px] flex-col gap-12 md:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.7 }}
            className="flex max-w-[820px] flex-col gap-5"
          >
            <Eyebrow>
              <RichTextContent path="alonecontent.arch_eyebrow" label="Architecture Eyebrow" as="span" className={EYEBROW_CLASS} />
            </Eyebrow>
            <RichTextContent
              path="alonecontent.arch_headline"
              label="Architecture Headline"
              as="h2"
              className="font-heading text-[clamp(33px,3.69vw,56px)] font-medium leading-[1.1] tracking-[-0.04em] text-[rgb(var(--ala-text))]"
            />
            <RichTextContent
              path="alonecontent.arch_body"
              label="Architecture Body"
              as="div"
              className="space-y-4 font-heading text-lg font-normal leading-[1.6] text-[rgb(var(--ala-text-muted))] [&_p]:mb-0"
            />
          </motion.div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <LifecycleCard
              index={0}
              icon={Database}
              num={<RichTextContent path="alonecontent.arch_1_num" label="Architecture 1 Number" as="span" className={STAGE_NUM_CLASS} />}
              title={<RichTextContent path="alonecontent.arch_1_title" label="Architecture 1 Title" as="h3" className={STAGE_TITLE_CLASS} />}
              body={<RichTextContent path="alonecontent.arch_1_body" label="Architecture 1 Body" as="p" className={CARD_BODY_CLASS} />}
            />
            <LifecycleCard
              index={1}
              icon={AppWindow}
              num={<RichTextContent path="alonecontent.arch_2_num" label="Architecture 2 Number" as="span" className={STAGE_NUM_CLASS} />}
              title={<RichTextContent path="alonecontent.arch_2_title" label="Architecture 2 Title" as="h3" className={STAGE_TITLE_CLASS} />}
              body={<RichTextContent path="alonecontent.arch_2_body" label="Architecture 2 Body" as="p" className={CARD_BODY_CLASS} />}
            />
            <LifecycleCard
              index={2}
              icon={Layers}
              num={<RichTextContent path="alonecontent.arch_3_num" label="Architecture 3 Number" as="span" className={STAGE_NUM_CLASS} />}
              title={<RichTextContent path="alonecontent.arch_3_title" label="Architecture 3 Title" as="h3" className={STAGE_TITLE_CLASS} />}
              body={<RichTextContent path="alonecontent.arch_3_body" label="Architecture 3 Body" as="p" className={CARD_BODY_CLASS} />}
            />
          </div>
        </div>
      </section>

      {/* AL-ONE overview & purpose */}
      <section className="mx-auto w-full max-w-[1440px] bg-[rgb(var(--ala-background))] px-6 py-20 md:px-[clamp(16px,6.7vw,96px)] md:py-[clamp(80px,10vw,150px)]">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-[clamp(26px,6vw,96px)]">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.8, ease: EASE }}
            className="flex flex-col gap-6"
          >
            <Eyebrow>
              <RichTextContent path="alonecontent.intro_eyebrow" label="Intro Eyebrow" as="span" className={EYEBROW_CLASS} />
            </Eyebrow>
            <RichTextContent
              path="alonecontent.intro_headline"
              label="Intro Headline"
              as="h2"
              className="font-heading text-[clamp(33px,4.17vw,60px)] font-medium leading-[1.08] tracking-[-0.03em] text-[rgb(var(--ala-text))]"
            />
            <RichTextContent
              path="alonecontent.intro_body"
              label="Intro Body"
              as="div"
              className="space-y-6 font-heading text-lg font-normal leading-[1.6] text-[rgb(var(--ala-text-muted))] [&_p]:mb-0"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
            className="overflow-clip rounded-[24px] bg-[#f6f7f9] pb-10"
          >
            <AlOneLayerStack />
          </motion.div>
        </div>
      </section>

      {/* AI with an audit trail */}
      <section className="relative w-full overflow-clip bg-[rgb(var(--ala-dark))] px-6 py-20 md:px-[clamp(16px,6.7vw,96px)] md:py-[clamp(80px,11vw,160px)]">
        <div className="pointer-events-none absolute inset-0" aria-hidden>
          <div className="absolute -left-40 top-1/3 h-[480px] w-[480px] rounded-full bg-[rgb(var(--ala-secondary))]/15 blur-[140px]" />
        </div>
        <div className="relative z-10 mx-auto grid w-full max-w-[1248px] grid-cols-1 items-center gap-14 lg:grid-cols-[1fr_1.05fr] lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.8, ease: EASE }}
            className="flex flex-col gap-6"
          >
            <Eyebrow>
              <RichTextContent path="alonecontent.ai_eyebrow" label="AI Eyebrow" as="span" className={EYEBROW_LIGHT_CLASS} />
            </Eyebrow>
            <RichTextContent
              path="alonecontent.ai_headline"
              label="AI Headline"
              as="h2"
              className="font-heading text-[clamp(33px,3.69vw,56px)] font-medium leading-[1.1] tracking-[-0.04em] text-[rgb(var(--ala-on-primary))]"
            />
            <RichTextContent
              path="alonecontent.ai_body"
              label="AI Body"
              as="div"
              className="space-y-5 font-heading text-lg font-normal leading-[1.6] text-[rgb(var(--ala-on-primary))]/75 [&_p]:mb-0 [&_p]:text-[rgb(var(--ala-on-primary))]/75"
            />
            <ul className="mt-2 flex flex-col gap-3">
              <AiPoint>
                <RichTextContent path="alonecontent.ai_point_1" label="AI Point 1" as="span" className={AI_POINT_CLASS} />
              </AiPoint>
              <AiPoint>
                <RichTextContent path="alonecontent.ai_point_2" label="AI Point 2" as="span" className={AI_POINT_CLASS} />
              </AiPoint>
              <AiPoint>
                <RichTextContent path="alonecontent.ai_point_3" label="AI Point 3" as="span" className={AI_POINT_CLASS} />
              </AiPoint>
            </ul>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 60, rotateX: 8 }}
            whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
            style={{ transformPerspective: 1200 }}
          >
            <AuditTrailPanel />
          </motion.div>
        </div>
      </section>

      {/* Advanced analytics, what-if & verification */}
      <section className="w-full bg-[rgb(var(--ala-surface))] px-6 py-20 md:px-[clamp(16px,6.7vw,96px)] md:py-[clamp(80px,10vw,140px)]">
        <div className="mx-auto flex w-full max-w-[1248px] flex-col gap-12 md:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.7 }}
            className="flex max-w-[820px] flex-col gap-5"
          >
            <Eyebrow>
              <RichTextContent path="alonecontent.analytics_eyebrow" label="Analytics Eyebrow" as="span" className={EYEBROW_CLASS} />
            </Eyebrow>
            <RichTextContent
              path="alonecontent.analytics_headline"
              label="Analytics Headline"
              as="h2"
              className="font-heading text-[clamp(33px,3.69vw,56px)] font-medium leading-[1.1] tracking-[-0.04em] text-[rgb(var(--ala-text))]"
            />
            <RichTextContent
              path="alonecontent.analytics_body"
              label="Analytics Body"
              as="div"
              className="space-y-4 font-heading text-lg font-normal leading-[1.6] text-[rgb(var(--ala-text-muted))] [&_p]:mb-0"
            />
          </motion.div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
            <LifecycleCard
              index={0}
              icon={Gauge}
              num={<RichTextContent path="alonecontent.analytics_1_num" label="Analytics 1 Number" as="span" className={STAGE_NUM_CLASS} />}
              title={<RichTextContent path="alonecontent.analytics_1_title" label="Analytics 1 Title" as="h3" className={STAGE_TITLE_CLASS} />}
              body={<RichTextContent path="alonecontent.analytics_1_body" label="Analytics 1 Body" as="p" className={CARD_BODY_CLASS} />}
            />
            <LifecycleCard
              index={1}
              icon={Search}
              num={<RichTextContent path="alonecontent.analytics_2_num" label="Analytics 2 Number" as="span" className={STAGE_NUM_CLASS} />}
              title={<RichTextContent path="alonecontent.analytics_2_title" label="Analytics 2 Title" as="h3" className={STAGE_TITLE_CLASS} />}
              body={<RichTextContent path="alonecontent.analytics_2_body" label="Analytics 2 Body" as="p" className={CARD_BODY_CLASS} />}
            />
            <LifecycleCard
              index={2}
              icon={Calculator}
              num={<RichTextContent path="alonecontent.analytics_3_num" label="Analytics 3 Number" as="span" className={STAGE_NUM_CLASS} />}
              title={<RichTextContent path="alonecontent.analytics_3_title" label="Analytics 3 Title" as="h3" className={STAGE_TITLE_CLASS} />}
              body={<RichTextContent path="alonecontent.analytics_3_body" label="Analytics 3 Body" as="p" className={CARD_BODY_CLASS} />}
            />
            <LifecycleCard
              index={3}
              icon={FileSearch}
              num={<RichTextContent path="alonecontent.analytics_4_num" label="Analytics 4 Number" as="span" className={STAGE_NUM_CLASS} />}
              title={<RichTextContent path="alonecontent.analytics_4_title" label="Analytics 4 Title" as="h3" className={STAGE_TITLE_CLASS} />}
              body={<RichTextContent path="alonecontent.analytics_4_body" label="Analytics 4 Body" as="p" className={CARD_BODY_CLASS} />}
            />
          </div>
        </div>
      </section>

      {/* Trust pillars */}
      <section className="w-full bg-[rgb(var(--ala-background))] px-6 py-20 md:px-[clamp(16px,6.7vw,96px)] md:py-[clamp(80px,10vw,140px)]">
        <div className="mx-auto flex w-full max-w-[1248px] flex-col gap-14">
          <div className="grid grid-cols-1 items-end gap-10 lg:grid-cols-[1.3fr_1fr]">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={alaViewport}
              transition={{ duration: 0.7 }}
              className="flex flex-col gap-5"
            >
              <Eyebrow>
                <RichTextContent path="alonecontent.pillars_eyebrow" label="Pillars Eyebrow" as="span" className={EYEBROW_CLASS} />
              </Eyebrow>
              <RichTextContent
                path="alonecontent.pillars_headline"
                label="Pillars Headline"
                as="h2"
                className="font-heading text-[clamp(33px,3.69vw,56px)] font-medium leading-[1.1] tracking-[-0.04em] text-[rgb(var(--ala-text))]"
              />
            </motion.div>
            <div className="flex flex-col gap-2 lg:items-end lg:text-right">
              <EditableToken
                path="alonecontent.accuracy_value"
                type="text"
                label="Accuracy Value"
                as="div"
              >
                <CountUp
                  value={accuracy}
                  className="font-heading text-[clamp(72px,9vw,140px)] font-bold leading-[0.9] tracking-[-0.05em] text-[rgb(var(--ala-secondary))]"
                />
                <span className="sr-only">{accuracy}</span>
              </EditableToken>
              <RichTextContent
                path="alonecontent.accuracy_label"
                label="Accuracy Label"
                as="p"
                className="font-heading text-base font-medium uppercase tracking-[0.12em] text-[rgb(var(--ala-text-muted))]"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            <PillarCard
              index={0}
              icon={ShieldCheck}
              title={<RichTextContent path="alonecontent.pillar_1_title" label="Pillar 1 Title" as="h3" className={PILLAR_TITLE_CLASS} />}
              body={<RichTextContent path="alonecontent.pillar_1_body" label="Pillar 1 Body" as="p" className={CARD_BODY_CLASS} />}
            />
            <PillarCard
              index={1}
              icon={FileSearch}
              title={<RichTextContent path="alonecontent.pillar_2_title" label="Pillar 2 Title" as="h3" className={PILLAR_TITLE_CLASS} />}
              body={<RichTextContent path="alonecontent.pillar_2_body" label="Pillar 2 Body" as="p" className={CARD_BODY_CLASS} />}
            />
            <PillarCard
              index={2}
              icon={Target}
              title={<RichTextContent path="alonecontent.pillar_3_title" label="Pillar 3 Title" as="h3" className={PILLAR_TITLE_CLASS} />}
              body={<RichTextContent path="alonecontent.pillar_3_body" label="Pillar 3 Body" as="p" className={CARD_BODY_CLASS} />}
            />
            <PillarCard
              index={3}
              icon={Lock}
              title={<RichTextContent path="alonecontent.pillar_4_title" label="Pillar 4 Title" as="h3" className={PILLAR_TITLE_CLASS} />}
              body={<RichTextContent path="alonecontent.pillar_4_body" label="Pillar 4 Body" as="p" className={CARD_BODY_CLASS} />}
            />
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="relative w-full overflow-clip bg-[rgb(var(--ala-dark))] py-20 md:py-[clamp(80px,11vw,160px)]">
        <div className="pointer-events-none absolute inset-0 opacity-[0.08]" aria-hidden>
          <div className="absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[rgb(var(--ala-secondary))] blur-[150px]" />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={alaViewport}
          transition={{ duration: 0.7 }}
          className="relative z-10 mx-auto flex max-w-[1440px] flex-col items-center gap-8 px-6 text-center md:px-[clamp(16px,3.3vw,48px)]"
        >
          <Eyebrow>
            <RichTextContent path="alonecontent.cta_eyebrow" label="CTA Eyebrow" as="span" className={EYEBROW_LIGHT_CLASS} />
          </Eyebrow>
          <RichTextContent
            path="alonecontent.cta_headline"
            label="CTA Headline"
            as="h2"
            className="max-w-[1000px] font-heading text-[clamp(36px,5vw,76px)] font-medium leading-[1.02] tracking-[-0.03em] text-[rgb(var(--ala-on-primary))]"
          />
          <RichTextContent
            path="alonecontent.cta_body"
            label="CTA Body"
            as="p"
            className="max-w-[640px] font-heading text-[clamp(18px,1.5vw,22px)] font-normal leading-[1.55] text-[rgb(var(--ala-on-primary))]/80 [&_p]:text-[rgb(var(--ala-on-primary))]/80"
          />
          <div className="flex flex-wrap items-center justify-center gap-4">
            <EditableSlotGroup groupPath="alonecontent.investors_cta" definition={investorsCtaDef}>
              <a
                href={investorsCta.url || '/investors'}
                target={investorsCta._target || '_self'}
                className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-[rgb(var(--ala-secondary))] px-7 py-[15px] font-heading text-sm font-semibold uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-secondary))] transition-colors hover:bg-[#3a85d0]"
              >
                {investorsCta.label || 'Explore investing with us'}
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="alonecontent.contact_cta" definition={contactCtaDef}>
              <a
                href={contactCta.url || '/investors#investor-contact'}
                target={contactCta._target || '_self'}
                className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-[rgb(var(--ala-on-primary))]/30 px-7 py-[15px] font-heading text-sm font-semibold uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-primary))] transition-colors hover:bg-[rgb(var(--ala-on-primary))]/10"
              >
                {contactCta.label || 'Talk to capital markets'}
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </a>
            </EditableSlotGroup>
          </div>
        </motion.div>
      </section>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AlOneContent',
  displayName: 'AL-ONE Content',
  tokens: {
    'alonecontent.intro_eyebrow': {
      label: 'Intro Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Introducing AL-ONE</p>',
    },
    'alonecontent.intro_headline': {
      label: 'Intro Headline',
      type: 'richtext',
      defaultValue: '<p>One platform. Every asset. Clearer decisions.</p>',
    },
    'alonecontent.intro_body': {
      label: 'Intro Body',
      type: 'richtext',
      defaultValue:
        '<p>AL-ONE is American Landmark\'s proprietary, in-house asset intelligence platform — designed to centralize and accumulate portfolio data so executives can make faster, more confident decisions.</p><p>By bringing every data point into a single, verifiable source of truth, AL-ONE turns complex portfolio information into clear action — for our teams, our partners, and our investors.</p>',
    },
    'alonecontent.arch_eyebrow': {
      label: 'Architecture Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Core architecture</p>',
    },
    'alonecontent.arch_headline': {
      label: 'Architecture Headline',
      type: 'richtext',
      defaultValue: '<p>A three-pronged approach to AI.</p>',
    },
    'alonecontent.arch_body': {
      label: 'Architecture Body',
      type: 'richtext',
      defaultValue:
        '<p>American Landmark\'s AI strategy integrates traditional property management systems, specialized niche applications, and in-house platform development — so the right intelligence lives where teams already work, and AL-ONE ties it together.</p>',
    },
    'alonecontent.arch_1_num': { label: 'Architecture 1 Number', type: 'richtext', defaultValue: '<p>01</p>' },
    'alonecontent.arch_1_title': {
      label: 'Architecture 1 Title',
      type: 'richtext',
      defaultValue: '<p>Property management systems</p>',
    },
    'alonecontent.arch_1_body': {
      label: 'Architecture 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>Integrate traditional PMS data so occupancy, leasing, and operations feed a single, trusted portfolio view.</p>',
    },
    'alonecontent.arch_2_num': { label: 'Architecture 2 Number', type: 'richtext', defaultValue: '<p>02</p>' },
    'alonecontent.arch_2_title': {
      label: 'Architecture 2 Title',
      type: 'richtext',
      defaultValue: '<p>Niche applications</p>',
    },
    'alonecontent.arch_2_body': {
      label: 'Architecture 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>Connect specialized tools across the organization so high-value workflows stay sharp without fragmenting the data model.</p>',
    },
    'alonecontent.arch_3_num': { label: 'Architecture 3 Number', type: 'richtext', defaultValue: '<p>03</p>' },
    'alonecontent.arch_3_title': {
      label: 'Architecture 3 Title',
      type: 'richtext',
      defaultValue: '<p>In-house platform</p>',
    },
    'alonecontent.arch_3_body': {
      label: 'Architecture 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>Build proprietary capabilities on AL-ONE — from portfolio analytics to decision-ready intelligence — that no off-the-shelf stack can match.</p>',
    },
    'alonecontent.ai_eyebrow': {
      label: 'AI Eyebrow',
      type: 'richtext',
      defaultValue: '<p>AI you can audit</p>',
    },
    'alonecontent.ai_headline': {
      label: 'AI Headline',
      type: 'richtext',
      defaultValue: '<p>The power of AI. None of the guesswork.</p>',
    },
    'alonecontent.ai_body': {
      label: 'AI Body',
      type: 'richtext',
      defaultValue:
        '<p>AL-ONE leverages AI to analyze portfolio data, create action lists, and surface recommendations. Unlike general-purpose AI tools, it is grounded in verified sources — every output can be traced, reviewed, and acted on with confidence.</p>',
    },
    'alonecontent.ai_point_1': {
      label: 'AI Point 1',
      type: 'richtext',
      defaultValue: '<p>AI-driven analysis across every asset</p>',
    },
    'alonecontent.ai_point_2': {
      label: 'AI Point 2',
      type: 'richtext',
      defaultValue: '<p>Prioritized action lists and recommendations</p>',
    },
    'alonecontent.ai_point_3': {
      label: 'AI Point 3',
      type: 'richtext',
      defaultValue: '<p>A clear, transparent audit trail for every data point</p>',
    },
    'alonecontent.analytics_eyebrow': {
      label: 'Analytics Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Advanced analytics</p>',
    },
    'alonecontent.analytics_headline': {
      label: 'Analytics Headline',
      type: 'richtext',
      defaultValue: '<p>Model outcomes. Verify every number.</p>',
    },
    'alonecontent.analytics_body': {
      label: 'Analytics Body',
      type: 'richtext',
      defaultValue:
        '<p>From scorecards and mystery shopping to live what-if modeling and third-party market data, AL-ONE pairs forward-looking analytics with a prove-it layer that keeps every formula tied to its source.</p>',
    },
    'alonecontent.analytics_1_num': { label: 'Analytics 1 Number', type: 'richtext', defaultValue: '<p>01</p>' },
    'alonecontent.analytics_1_title': {
      label: 'Analytics 1 Title',
      type: 'richtext',
      defaultValue: '<p>Scorecards &amp; mystery shops</p>',
    },
    'alonecontent.analytics_1_body': {
      label: 'Analytics 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>Track operational scorecards and integrate mystery shopping data so quality and service stay visible alongside financials.</p>',
    },
    'alonecontent.analytics_2_num': { label: 'Analytics 2 Number', type: 'richtext', defaultValue: '<p>02</p>' },
    'alonecontent.analytics_2_title': {
      label: 'Analytics 2 Title',
      type: 'richtext',
      defaultValue: '<p>Live what-if modeling</p>',
    },
    'alonecontent.analytics_2_body': {
      label: 'Analytics 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>Instantly project NOI shifts based on occupancy and concession adjustments — before you commit to a plan.</p>',
    },
    'alonecontent.analytics_3_num': { label: 'Analytics 3 Number', type: 'richtext', defaultValue: '<p>03</p>' },
    'alonecontent.analytics_3_title': {
      label: 'Analytics 3 Title',
      type: 'richtext',
      defaultValue: '<p>Market room data</p>',
    },
    'alonecontent.analytics_3_body': {
      label: 'Analytics 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>Bring external market intelligence from Green Street, CoStar, and Apartment IQ into the market room alongside internal portfolio data.</p>',
    },
    'alonecontent.analytics_4_num': { label: 'Analytics 4 Number', type: 'richtext', defaultValue: '<p>04</p>' },
    'alonecontent.analytics_4_title': {
      label: 'Analytics 4 Title',
      type: 'richtext',
      defaultValue: '<p>Prove-it verification</p>',
    },
    'alonecontent.analytics_4_body': {
      label: 'Analytics 4 Body',
      type: 'richtext',
      defaultValue:
        '<p>Verify occupancy formulas directly against source spreadsheets for 100% accuracy — with robust loan covenant tracking built in.</p>',
    },
    'alonecontent.pillars_eyebrow': {
      label: 'Pillars Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Why it matters</p>',
    },
    'alonecontent.pillars_headline': {
      label: 'Pillars Headline',
      type: 'richtext',
      defaultValue: '<p>Built for decisions that have to be right.</p>',
    },
    'alonecontent.accuracy_value': { label: 'Accuracy Value', type: 'text', defaultValue: '100%' },
    'alonecontent.accuracy_label': {
      label: 'Accuracy Label',
      type: 'richtext',
      defaultValue: '<p>Accuracy, by design</p>',
    },
    'alonecontent.pillar_1_title': {
      label: 'Pillar 1 Title',
      type: 'richtext',
      defaultValue: '<p>Zero Hallucinations</p>',
    },
    'alonecontent.pillar_1_body': {
      label: 'Pillar 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>Hard-coded logic keeps the AI grounded in real data, so it never invents numbers or conclusions.</p>',
    },
    'alonecontent.pillar_2_title': {
      label: 'Pillar 2 Title',
      type: 'richtext',
      defaultValue: '<p>Prove-It Audit Trail</p>',
    },
    'alonecontent.pillar_2_body': {
      label: 'Pillar 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>Every data point — including occupancy formulas — can be traced back to its source spreadsheet for full visibility.</p>',
    },
    'alonecontent.pillar_3_title': {
      label: 'Pillar 3 Title',
      type: 'richtext',
      defaultValue: '<p>100% Accuracy</p>',
    },
    'alonecontent.pillar_3_body': {
      label: 'Pillar 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>Verified inputs, deterministic outputs, and loan covenant tracking mean teams can act on AL-ONE with complete confidence.</p>',
    },
    'alonecontent.pillar_4_title': {
      label: 'Pillar 4 Title',
      type: 'richtext',
      defaultValue: '<p>Proprietary Advantage</p>',
    },
    'alonecontent.pillar_4_body': {
      label: 'Pillar 4 Body',
      type: 'richtext',
      defaultValue:
        '<p>Designed and built in-house, AL-ONE is a capability unique to American Landmark and our partners.</p>',
    },
    'alonecontent.cta_eyebrow': {
      label: 'CTA Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Capital Partners</p>',
    },
    'alonecontent.cta_headline': {
      label: 'CTA Headline',
      type: 'richtext',
      defaultValue: '<p>Better data. Stronger partnerships.</p>',
    },
    'alonecontent.cta_body': {
      label: 'CTA Body',
      type: 'richtext',
      defaultValue:
        '<p>Our capital markets team partners with institutional and private investors to pursue value-add multifamily opportunities across the Sun Belt — with full transparency into how every asset performs.</p>',
    },
  },
  slotGroups: {
    'alonecontent.investors_cta': investorsCtaDef,
    'alonecontent.contact_cta': contactCtaDef,
  },
};

(AlOneContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AlOneContent;
